import { Injectable } from '@angular/core';
import { CropSelectionRequest, EnhancementLevel } from '../models/job-submit.models';

interface JobProcessingState {
  jobId: number;
  isEnhancements: boolean;
  isCropping: boolean;
  isBackgroundExtraction: boolean;
  isBestPick: boolean;
  enhancementLevel: EnhancementLevel | null;
  cropSelections: CropSelectionRequest[];
  cropsPerImagePose: number;
}

@Injectable({
  providedIn: 'root'
})
export class JobProcessingStateService {
  private readonly storageKeyPrefix = 'flwx_job_processing_';

  setJobFlags(payload: {
    jobId: number;
    isEnhancements: boolean;
    isCropping: boolean;
    isBackgroundExtraction: boolean;
    isBestPick: boolean;
    cropsPerImagePose?: number | null;
  }): void {
    const current = this.getState(payload.jobId);

    this.saveState({
      ...current,
      jobId: payload.jobId,
      isEnhancements: payload.isEnhancements,
      isCropping: payload.isCropping,
      isBackgroundExtraction: payload.isBackgroundExtraction,
      isBestPick: payload.isBestPick,
      cropsPerImagePose: (payload.cropsPerImagePose != null && payload.cropsPerImagePose > 0)
        ? Math.min(4, Math.max(1, payload.cropsPerImagePose))
        : current.cropsPerImagePose
    });
  }

  setCropsCount(jobId: number, count: number): void {
    const current = this.getState(jobId);
    this.saveState({
      ...current,
      jobId,
      cropsPerImagePose: Math.min(4, Math.max(1, count))
    });
  }

  setEnhancementLevel(jobId: number, enhancementLevel: EnhancementLevel): void {
    const current = this.getState(jobId);
    this.saveState({
      ...current,
      jobId,
      enhancementLevel
    });
  }

  setCropSelections(jobId: number, cropSelections: CropSelectionRequest[]): void {
    const current = this.getState(jobId);
    this.saveState({
      ...current,
      jobId,
      cropSelections
    });
  }

  getState(jobId: number): JobProcessingState {
    try {
      const raw = localStorage.getItem(this.getStorageKey(jobId));
      if (!raw) {
        return this.getDefaultState(jobId);
      }

      return {
        ...this.getDefaultState(jobId),
        ...(JSON.parse(raw) as JobProcessingState)
      };
    } catch {
      return this.getDefaultState(jobId);
    }
  }

  clearState(jobId: number): void {
    localStorage.removeItem(this.getStorageKey(jobId));
  }

  setActiveCreateJobId(jobId: number): void {
    localStorage.setItem(this.storageKeyPrefix + 'active_create_id', jobId.toString());
  }

  getActiveCreateJobId(): number | null {
    const raw = localStorage.getItem(this.storageKeyPrefix + 'active_create_id');
    return raw ? parseInt(raw, 10) : null;
  }

  clearActiveCreateJobId(): void {
    localStorage.removeItem(this.storageKeyPrefix + 'active_create_id');
  }

  private saveState(state: JobProcessingState): void {
    localStorage.setItem(this.getStorageKey(state.jobId), JSON.stringify(state));
  }

  private getStorageKey(jobId: number): string {
    return `${this.storageKeyPrefix}${jobId}`;
  }

  private getDefaultState(jobId: number): JobProcessingState {
    return {
      jobId,
      isEnhancements: false,
      isCropping: false,
      isBackgroundExtraction: false,
      isBestPick: false,
      enhancementLevel: null,
      cropSelections: [],
      cropsPerImagePose: 4
    };
  }
}